import type { ReactElement } from 'react';
import { formatPercent, formatPrice, formatQuantity, formatUsdt, isNegative } from '../lib/money';
import type { DecimalInput } from '../lib/money';

/**
 * Money renderers.
 *
 * Every one of these takes the decimal STRING straight off the wire. Nothing
 * in this file converts to `number`; `lib/money.ts` does the formatting with
 * decimal.js. A `null` renders as «—», never as `NaN` or an empty cell.
 */

export interface MoneyProps {
  readonly value: DecimalInput;
  readonly fractionDigits?: number;
}

export function Usdt({ value, fractionDigits = 2 }: MoneyProps): ReactElement {
  return <span className="num">{formatUsdt(value, fractionDigits)}</span>;
}

export function Price({ value, fractionDigits = 1 }: MoneyProps): ReactElement {
  return <span className="num">{formatPrice(value, fractionDigits)}</span>;
}

export function Quantity({ value, fractionDigits = 4 }: MoneyProps): ReactElement {
  return <span className="num">{formatQuantity(value, fractionDigits)}</span>;
}

export function Percent({ value, fractionDigits = 2 }: MoneyProps): ReactElement {
  return <span className="num">{formatPercent(value, fractionDigits)}</span>;
}

/** Signed money: red when negative, green otherwise, plus an explicit sign. */
export function Pnl({ value, fractionDigits = 2 }: MoneyProps): ReactElement {
  if (value === null || value === undefined || value === '') {
    return <span className="num muted">—</span>;
  }
  const negative = isNegative(value);
  return (
    <span className={negative ? 'num pnl-negative' : 'num pnl-positive'}>
      <span className="visually-hidden">{negative ? 'убыток ' : 'прибыль '}</span>
      {formatUsdt(value, fractionDigits)}
    </span>
  );
}
